/**
 * Real-time P&L Snapshot Widget - Premium Liquid Crystal Design
 * ===============================================================
 * Live revenue vs expenses tracking with margin analysis
 * and cost breakdown by category. Apple-inspired glassmorphism.
 */

import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, ArrowUpRight, ArrowDownRight,
  ChevronRight, PieChart, Wallet, CreditCard, Receipt, Building,
  Users, Package, Zap, Car, ShoppingBag, Utensils
} from 'lucide-react';
import { ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, Tooltip } from 'recharts';
import { Progress } from '@/components/ui/progress';

// Demo P&L data
const PL_DATA = {
  hospitality: {
    revenue: { mtd: 185420, target: 200000, lastMonth: 172350, ytd: 1854200 },
    expenses: {
      cogs: { amount: 51918, percent: 28, target: 28, label: 'Cost of Goods', icon: Package },
      labor: { amount: 59334, percent: 32, target: 32, label: 'Labor', icon: Users },
      rent: { amount: 18542, percent: 10, target: 10, label: 'Rent & Utilities', icon: Building },
      marketing: { amount: 5563, percent: 3, target: 4, label: 'Marketing', icon: Zap },
      other: { amount: 9271, percent: 5, target: 5, label: 'Other', icon: Receipt },
    },
    grossMargin: 72,
    netMargin: 22,
    targetNetMargin: 25,
  },
  retail: {
    revenue: { mtd: 245680, target: 260000, lastMonth: 228450, ytd: 2456800 },
    expenses: {
      cogs: { amount: 147408, percent: 60, target: 58, label: 'Cost of Goods', icon: Package },
      labor: { amount: 34395, percent: 14, target: 15, label: 'Labor', icon: Users },
      rent: { amount: 24568, percent: 10, target: 10, label: 'Rent & Utilities', icon: Building },
      marketing: { amount: 12284, percent: 5, target: 6, label: 'Marketing', icon: Zap },
      other: { amount: 7370, percent: 3, target: 3, label: 'Other', icon: Receipt },
    },
    grossMargin: 40,
    netMargin: 8,
    targetNetMargin: 10,
  },
  professional: {
    revenue: { mtd: 312500, target: 325000, lastMonth: 298750, ytd: 3125000 },
    expenses: {
      labor: { amount: 187500, percent: 60, target: 58, label: 'Staff Costs', icon: Users },
      rent: { amount: 31250, percent: 10, target: 10, label: 'Office & Utilities', icon: Building },
      software: { amount: 15625, percent: 5, target: 5, label: 'Software & Tools', icon: Zap },
      marketing: { amount: 9375, percent: 3, target: 4, label: 'Marketing', icon: Zap },
      other: { amount: 15625, percent: 5, target: 5, label: 'Other', icon: Receipt },
    },
    grossMargin: 40,
    netMargin: 17,
    targetNetMargin: 20,
  },
};

const COLORS = ['#f59e0b', '#3b82f6', '#8b5cf6', '#10b981', '#6b7280'];
const GRADIENT_COLORS = [
  { from: 'from-amber-400', to: 'to-orange-500', shadow: 'shadow-amber-500/25' },
  { from: 'from-blue-400', to: 'to-indigo-500', shadow: 'shadow-blue-500/25' },
  { from: 'from-violet-400', to: 'to-purple-500', shadow: 'shadow-violet-500/25' },
  { from: 'from-emerald-400', to: 'to-[#0066CC]', shadow: 'shadow-emerald-500/25' },
  { from: 'from-gray-400', to: 'to-slate-500', shadow: 'shadow-gray-500/25' },
];

const PLSnapshotWidget = ({
  industry = 'hospitality',
  className = '',
  compact = false,
  onViewDetails
}) => {
  const [viewMode, setViewMode] = useState('current');
  const data = PL_DATA[industry] || PL_DATA.hospitality;
  
  const totalExpenses = Object.values(data.expenses).reduce((sum, e) => sum + e.amount, 0);
  const netProfit = data.revenue.mtd - totalExpenses;
  const revenueProgress = (data.revenue.mtd / data.revenue.target) * 100;
  const revenueVsLastMonth = ((data.revenue.mtd - data.revenue.lastMonth) / data.revenue.lastMonth * 100).toFixed(1);
  
  const expenseChartData = Object.entries(data.expenses).map(([key, value], idx) => ({
    name: value.label,
    value: value.amount,
    percent: value.percent,
    color: COLORS[idx]
  }));

  if (compact) {
    return (
      <div 
        className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-5 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] hover:-translate-y-0.5 ${className}`} 
        data-testid="pl-snapshot-compact"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-[#0066CC] flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <DollarSign className="w-4 h-4 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 tracking-tight">P&L Snapshot</h4>
            </div>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${netProfit > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} border ${netProfit > 0 ? 'border-emerald-200' : 'border-red-200'} whitespace-nowrap`}>
              {data.netMargin}% margin
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-50/80 to-blue-50/50 border border-emerald-100">
              <p className="text-xl font-bold text-emerald-700">${(data.revenue.mtd / 1000).toFixed(0)}K</p>
              <p className="text-[10px] text-emerald-600 font-medium">Revenue MTD</p>
            </div>
            <div className={`p-3 rounded-2xl border ${netProfit > 0 ? 'bg-gradient-to-br from-blue-50/80 to-indigo-50/50 border-blue-100' : 'bg-gradient-to-br from-red-50/80 to-rose-50/50 border-red-100'}`}>
              <p className={`text-xl font-bold ${netProfit > 0 ? 'text-blue-700' : 'text-red-700'}`}>
                ${(netProfit / 1000).toFixed(0)}K
              </p>
              <p className={`text-[10px] ${netProfit > 0 ? 'text-blue-600' : 'text-red-600'} font-medium`}>Net Profit</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-6 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] ${className}`} 
      data-testid="pl-snapshot-widget"
    >
      {/* Background gradient orb */}
      <div className="absolute -top-20 -right-20 w-56 h-56 bg-gradient-to-br from-emerald-100/50 to-teal-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-400 to-[#0066CC] flex items-center justify-center shadow-lg shadow-emerald-500/25">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <div className="flex items-center gap-2.5">
              <h3 className="font-semibold text-gray-900 tracking-tight">Real-time P&L</h3>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 whitespace-nowrap">MTD</span>
            </div>
          </div>
          <button 
            onClick={onViewDetails}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors whitespace-nowrap"
          >
            Full P&L <ChevronRight className="w-3 h-3 ml-1" />
          </button>
        </div>

        {/* Revenue Section */}
        <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-50/80 to-blue-50/50 border border-emerald-100 mb-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-emerald-800">Revenue</span>
            <div className="flex items-center gap-1.5 text-emerald-600 text-xs font-medium">
              {parseFloat(revenueVsLastMonth) >= 0 ? (
                <><ArrowUpRight className="w-3.5 h-3.5" /> +{revenueVsLastMonth}%</>
              ) : (
                <><ArrowDownRight className="w-3.5 h-3.5" /> {revenueVsLastMonth}%</>
              )}
              <span className="text-emerald-500">vs LM</span>
            </div>
          </div>
          <div className="flex items-end justify-between mb-3">
            <span className="text-3xl font-bold text-emerald-800">${(data.revenue.mtd / 1000).toFixed(0)}K</span>
            <span className="text-sm text-emerald-600">of ${(data.revenue.target / 1000).toFixed(0)}K target</span>
          </div>
          <Progress value={revenueProgress} className="h-2.5 mb-2" />
          <p className="text-xs text-emerald-600 font-medium">{revenueProgress.toFixed(0)}% to monthly target</p>
        </div>

        {/* Expense Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          {/* Pie Chart */}
          <div className="h-36 flex items-center justify-center">
            <ResponsiveContainer width="100%" height={144}>
              <RechartsPie>
                <Pie
                  data={expenseChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={55}
                  dataKey="value"
                  label={false}
                  strokeWidth={2}
                  stroke="white"
                >
                  {expenseChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => `$${(value / 1000).toFixed(1)}K`}
                  contentStyle={{ 
                    fontSize: '11px', 
                    borderRadius: '12px',
                    border: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}
                />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
          
          {/* Legend */}
          <div className="space-y-2 flex flex-col justify-center">
            {expenseChartData.map((expense, idx) => (
              <div key={`${expense?.id ?? expense?.name ?? expense?.title ?? 'expense'}-${idx}`} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: expense.color }} />
                <span className="text-xs text-gray-600 flex-1 truncate">{expense.name}</span>
                <span className="text-xs font-bold text-gray-900">{expense.percent}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Expense Details */}
        <div className="space-y-2 mb-5">
          {Object.entries(data.expenses).map(([key, expense], idx) => {
            const Icon = expense.icon;
            const isOverBudget = expense.percent > expense.target;
            const gradientStyle = GRADIENT_COLORS[idx % GRADIENT_COLORS.length];
            return (
              <div key={key} className="flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-r from-gray-50/80 to-white/60 border border-gray-100/50 transition-all hover:shadow-md hover:-translate-y-0.5">
                <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${gradientStyle.from} ${gradientStyle.to} flex items-center justify-center shadow-lg ${gradientStyle.shadow}`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 truncate">{expense.label}</span>
                    <span className="text-sm font-bold text-gray-900">${(expense.amount / 1000).toFixed(1)}K</span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className={`text-xs ${isOverBudget ? 'text-red-600' : 'text-gray-500'}`}>
                      {expense.percent}% of revenue (target: {expense.target}%)
                    </span>
                    {isOverBudget && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200 whitespace-nowrap">Over budget</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Net Profit Summary */}
        <div className={`p-4 rounded-2xl border ${netProfit > 0 ? 'bg-gradient-to-br from-blue-50/80 to-indigo-50/50 border-blue-100' : 'bg-gradient-to-br from-red-50/80 to-rose-50/50 border-red-100'}`}>
          <div className="flex items-center justify-between">
            <div>
              <span className={`text-sm font-medium ${netProfit > 0 ? 'text-blue-800' : 'text-red-800'}`}>Net Profit</span>
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-3xl font-bold ${netProfit > 0 ? 'text-blue-800' : 'text-red-800'}`}>
                  ${(netProfit / 1000).toFixed(0)}K
                </span>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${netProfit > 0 ? 'bg-blue-200/80 text-blue-800' : 'bg-red-200/80 text-red-800'} whitespace-nowrap`}>
                  {data.netMargin}% margin
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 font-medium">Target Margin</p>
              <p className={`text-2xl font-bold ${data.netMargin >= data.targetNetMargin ? 'text-emerald-600' : 'text-amber-600'}`}>
                {data.targetNetMargin}%
              </p>
              {data.netMargin < data.targetNetMargin && (
                <p className="text-[10px] text-amber-600 font-medium">
                  {(data.targetNetMargin - data.netMargin)}% below target
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PLSnapshotWidget;
